import {
  FetchError,
  FrontendApi,
  ResponseError,
  Session,
} from "@ory/client-fetch";
import { useCallback, useEffect, useState } from "react";

export default function useOrySession(
  oryClient: FrontendApi | null,
  logoutReturnTo: string,
  reportError: (error: unknown) => void
) {
  const [orySession, setOrySession] = useState<Session | undefined>();
  const [oryLogoutUrl, setOryLogoutUrl] = useState("");
  const [loadingOrySession, setLoadingOrySession] = useState(
    oryClient ? true : false
  );

  const storeOrySession = useCallback(
    async (session: Session | undefined): Promise<void> => {
      if (!oryClient) return;
      setOrySession(session);
      try {
        const { logout_url } = await oryClient.createBrowserLogoutFlow();
        setOryLogoutUrl(`${logout_url}&return_to=${logoutReturnTo}`);
      } catch (e) {
        console.error("Error creating logout flow: ", e);
        // report but do not fail
        reportError(e);
      }
    },
    [oryClient, logoutReturnTo, reportError]
  );

  // restore the ory session by asking kratos if we have a session
  // cookie and if it would kindly tell us what's in it.
  useEffect(() => {
    void (async () => {
      if (!oryClient) return;
      setLoadingOrySession(true);
      try {
        const session = await oryClient.toSession();
        await storeOrySession(session);
      } catch (e) {
        // a 401 just means there's no active session (e.g. on the login
        // page while logged out) - that's expected, not an error to report.
        // a FetchError means the request never reached kratos (offline,
        // DNS, ad-blocker, etc.) - a client-side network issue, not
        // something actionable on our end.
        const isExpected =
          (e instanceof ResponseError && e.response.status === 401) ||
          e instanceof FetchError;
        if (!isExpected) {
          console.error("Error fetching ory session: ", e);
          reportError(e);
        }
      } finally {
        setLoadingOrySession(false);
      }
    })();
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const oryLogout = useCallback(() => {
    if (!oryClient) return;
    setOrySession(undefined);
    if (oryLogoutUrl) window.location.href = oryLogoutUrl;
  }, [oryClient, oryLogoutUrl]);

  return {
    orySession,
    loadingOrySession,
    oryLogout,
  };
}
